/**
 * Connected-components + boundary extraction — the replacement for
 * cv2.findContours.  The Python pipeline only ever uses the LARGEST external
 * contour (`max(cnts, key=cv2.contourArea)`), so we don't reconstruct full
 * contour topology: we flood-fill to find the largest blob, then derive what the
 * callers actually need from it — its boundary points (for the convex hull and
 * min-area rect) and its bounding box.
 *
 * APPROXIMATION NOTE: cv2.contourArea returns the *polygon* area of the traced
 * contour, whereas we use the blob's filled pixel COUNT. For the large, solid
 * document blobs the pipeline gates on, these agree to within a one-pixel border
 * and never change which blob is "largest" or whether the area-fraction guards
 * (0.10 / 0.18 of frame) pass. Where the Python code compares contourArea to a
 * hull/min-rect area we keep the comparison self-consistent by deriving the hull
 * and rect from the SAME boundary points.
 */
import { Mask } from './image';
import { Point } from './geometry';

export interface Blob {
  /** Number of foreground pixels (≈ cv2.contourArea of the external contour). */
  area: number;
  /** Inclusive bounding box. */
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  /** Boundary pixels of the blob — input to convexHull / minAreaRect. */
  boundary: Point[];
  /** All foreground pixels as a label mask (1 inside the blob, else 0). */
  labelMask: Uint8Array;
}

/**
 * Largest 4-connected foreground (value>0) blob of a binary mask. Returns null
 * when the mask is empty. Equivalent to taking the largest external contour.
 */
export function largestBlob(mask: Mask): Blob | null {
  const { data, width: W, height: H } = mask;
  const visited = new Uint8Array(W * H);
  const stack = new Int32Array(W * H);
  let best: Blob | null = null;
  let bestArea = 0;

  for (let start = 0; start < W * H; start++) {
    if (data[start] === 0 || visited[start]) continue;
    // BFS/flood this component.
    let sp = 0;
    stack[sp++] = start;
    visited[start] = 1;
    let area = 0;
    let x0 = W, y0 = H, x1 = -1, y1 = -1;
    const pixels: number[] = [];
    while (sp > 0) {
      const idx = stack[--sp];
      const x = idx % W;
      const y = (idx / W) | 0;
      area++;
      pixels.push(idx);
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (y < y0) y0 = y;
      if (y > y1) y1 = y;
      if (x > 0 && data[idx - 1] && !visited[idx - 1]) { visited[idx - 1] = 1; stack[sp++] = idx - 1; }
      if (x < W - 1 && data[idx + 1] && !visited[idx + 1]) { visited[idx + 1] = 1; stack[sp++] = idx + 1; }
      if (y > 0 && data[idx - W] && !visited[idx - W]) { visited[idx - W] = 1; stack[sp++] = idx - W; }
      if (y < H - 1 && data[idx + W] && !visited[idx + W]) { visited[idx + W] = 1; stack[sp++] = idx + W; }
    }
    if (area > bestArea) {
      bestArea = area;
      const labelMask = new Uint8Array(W * H);
      for (const p of pixels) labelMask[p] = 1;
      best = { area, x0, y0, x1, y1, boundary: [], labelMask };
    }
  }
  if (best) best.boundary = boundaryPoints(best.labelMask, W, H);
  return best;
}

/**
 * Boundary pixels of a label mask: foreground pixels with at least one
 * background 4-neighbour (or on the image edge). This point set fully
 * determines the convex hull and min-area rect.
 */
export function boundaryPoints(label: Uint8Array, W: number, H: number): Point[] {
  const pts: Point[] = [];
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const i = y * W + x;
      if (!label[i]) continue;
      const edge =
        x === 0 || y === 0 || x === W - 1 || y === H - 1 ||
        !label[i - 1] || !label[i + 1] || !label[i - W] || !label[i + W];
      if (edge) pts.push({ x, y });
    }
  }
  return pts;
}

/** Fill a convex polygon into a label mask (1 inside) — like drawContours FILLED. */
export function fillPolygon(poly: Point[], W: number, H: number): Uint8Array {
  const out = new Uint8Array(W * H);
  let minY = H, maxY = 0;
  for (const p of poly) {
    minY = Math.min(minY, Math.floor(p.y));
    maxY = Math.max(maxY, Math.ceil(p.y));
  }
  minY = Math.max(0, minY);
  maxY = Math.min(H - 1, maxY);
  const n = poly.length;
  for (let y = minY; y <= maxY; y++) {
    const xs: number[] = [];
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const a = poly[j], b = poly[i];
      if ((a.y <= y && b.y > y) || (b.y <= y && a.y > y)) {
        const t = (y - a.y) / (b.y - a.y);
        xs.push(a.x + t * (b.x - a.x));
      }
    }
    xs.sort((p, q) => p - q);
    for (let k = 0; k + 1 < xs.length; k += 2) {
      const sx = Math.max(0, Math.ceil(xs[k]));
      const ex = Math.min(W - 1, Math.floor(xs[k + 1]));
      for (let x = sx; x <= ex; x++) out[y * W + x] = 1;
    }
  }
  return out;
}
